import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Check, AlertCircle, Loader2, Clock, Hash,
  MessageSquare, Users, ChevronRight, ChevronDown, ExternalLink,
  Search, Filter, Zap, Database, Settings, ArrowLeft,
  TrendingUp, Activity, GitPullRequest, CircleDot, Github,
  Lock, X, Eye, GitMerge, GitBranch, Play, CheckCircle2, XCircle,
  Cpu, Layers, Target, Bug, Sparkles
} from 'lucide-react';
import api from '@/api';
import InvestigationModal from './InvestigationModal';
import CausalChainAnalysis from './CausalChainAnalysis';
import { LinearLogo, BuildkiteLogo, CriblLogo, PagerDutyLogo } from './logos';

// GitHub Logo SVG
function GitHubLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  );
}

// Slack Logo SVG
function SlackLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  );
}

// Jira Logo SVG
function JiraLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0z"/>
    </svg>
  );
}

// Format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Severity badge
function SeverityBadge({ severity }) {
  const colors = {
    critical: 'bg-destructive/10 text-destructive',
    high: 'bg-warning/10 text-warning',
    medium: 'bg-warning/10 text-warning',
    low: 'bg-success/10 text-success'
  };

  if (!severity) return null;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[severity] || 'bg-gray-100 text-gray-700'}`}>
      {severity}
    </span>
  );
}

// State badge for PRs/Issues
function StateBadge({ state, merged }) {
  if (merged) {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
        <GitMerge className="w-3 h-3" />
        Merged
      </span>
    );
  }

  const colors = {
    open: 'bg-success/10 text-success',
    closed: 'bg-destructive/10 text-destructive',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[state] || 'bg-gray-100 text-gray-700'}`}>
      {state}
    </span>
  );
}

// Slack Thread card
function ThreadCard({ thread, onInvestigate }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg bg-white hover:border-blue-300 transition-colors">
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Hash className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-primary">{thread.channel}</span>
              {thread.incident_id && (
                <span className="px-2 py-0.5 bg-info/10 text-info text-xs font-mono rounded">
                  {thread.incident_id}
                </span>
              )}
              <SeverityBadge severity={thread.severity} />
            </div>

            <h4 className="font-medium text-gray-900 truncate">
              {thread.title || 'Incident Discussion'}
            </h4>

            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                {thread.message_count} messages
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {thread.participants?.length || 0} participants
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatTimeAgo(thread.updated)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {thread.url && (
              <a
                href={thread.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-purple-50"
                title="Open in Slack"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInvestigate(thread);
              }}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg flex items-center gap-1.5"
            >
              <Search className="w-3.5 h-3.5" />
              Investigate
            </button>
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {expanded && thread.key_findings && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <h5 className="text-xs font-medium text-gray-500 mb-1">Key Findings</h5>
          <p className="text-sm text-gray-700">{thread.key_findings}</p>
        </div>
      )}
    </div>
  );
}

// GitHub PR/Issue card
function GitHubItemCard({ item, onInvestigate }) {
  const isPR = item.type === 'pull_request';

  return (
    <div className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-lg ${isPR ? 'bg-success/10' : 'bg-info/10'}`}>
          {isPR ? (
            <GitPullRequest className={`w-4 h-4 ${item.merged ? 'text-primary' : item.state === 'open' ? 'text-success' : 'text-destructive'}`} />
          ) : (
            <CircleDot className={`w-4 h-4 ${item.state === 'open' ? 'text-success' : 'text-primary'}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs text-gray-500 font-mono">{item.repo}</span>
            <span className="text-xs text-gray-400">#{item.number}</span>
            <StateBadge state={item.state} merged={item.merged} />
          </div>
          <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
            {item.title}
          </h4>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              {item.author_avatar ? (
                <img src={item.author_avatar} alt="" className="w-4 h-4 rounded-full" />
              ) : null}
              {item.author}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {formatTimeAgo(item.updated_at)}
            </span>
            {isPR && item.changed_files > 0 && (
              <span className="text-gray-400">
                {item.changed_files} files
              </span>
            )}
            {!isPR && item.comments_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                {item.comments_count}
              </span>
            )}
          </div>
          {item.labels && item.labels.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.labels.slice(0, 4).map((label, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                >
                  {label}
                </span>
              ))}
              {item.labels.length > 4 && (
                <span className="text-xs text-gray-400">+{item.labels.length - 4}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={item.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Open in GitHub"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={() => onInvestigate(item)}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
          >
            <Search className="w-3.5 h-3.5" />
            Investigate
          </button>
        </div>
      </div>
    </div>
  );
}

// Workflow Run conclusion badge
function ConclusionBadge({ conclusion }) {
  const colors = {
    success: 'bg-success/10 text-success',
    failure: 'bg-destructive/10 text-destructive',
    cancelled: 'bg-gray-100 text-gray-700',
    skipped: 'bg-warning/10 text-warning',
  };

  const icons = {
    success: <CheckCircle2 className="w-3 h-3" />,
    failure: <XCircle className="w-3 h-3" />,
    cancelled: <X className="w-3 h-3" />,
    skipped: <Clock className="w-3 h-3" />,
  };

  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${colors[conclusion] || 'bg-gray-100 text-gray-700'}`}>
      {icons[conclusion]}
      {conclusion}
    </span>
  );
}

// Connection badge for showing linked entities
function ConnectionBadge({ type, count, icon: Icon }) {
  if (!count || count === 0) return null;

  return (
    <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-info text-xs rounded">
      <Icon className="w-3 h-3" />
      {count}
    </span>
  );
}

// GitHub Workflow Run card
function WorkflowRunCard({ run, onInvestigate, connections }) {
  const [expanded, setExpanded] = useState(false);

  // Count connections by type
  const linkedPRs = connections?.edges?.filter(e =>
    e.edge_type === 'TRIGGERED_BY' || e.edge_type === 'VALIDATES' || e.edge_type === 'RELATES_TO'
  )?.length || 0;
  const linkedIssues = connections?.edges?.filter(e =>
    e.edge_type === 'MENTIONS' || e.edge_type === 'FIXES'
  )?.length || 0;

  return (
    <div className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-1.5 rounded-lg ${run.conclusion === 'success' ? 'bg-success/10' : run.conclusion === 'failure' ? 'bg-destructive/10' : 'bg-gray-100'}`}>
            <Play className={`w-4 h-4 ${run.conclusion === 'success' ? 'text-success' : run.conclusion === 'failure' ? 'text-destructive' : 'text-gray-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs text-gray-500 font-mono">{run.repo}</span>
              <span className="text-xs text-gray-400">#{run.run_number}</span>
              <ConclusionBadge conclusion={run.conclusion} />
              {/* Show connection badges */}
              <ConnectionBadge type="pr" count={linkedPRs} icon={GitPullRequest} />
              <ConnectionBadge type="issue" count={linkedIssues} icon={CircleDot} />
            </div>
            <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
              {run.workflow_name}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">{run.display_title}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <GitBranch className="w-3.5 h-3.5" />
                {run.head_branch}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatTimeAgo(run.updated_at)}
              </span>
              <span className="text-gray-400">
                {run.event}
              </span>
              {run.jobs_count > 0 && (
                <span className="text-gray-400">
                  {run.jobs_count} jobs
                </span>
              )}
            </div>
            {/* Show failed jobs if any */}
            {run.failed_jobs && run.failed_jobs.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {run.failed_jobs.map((job, i) => (
                  <span
                    key={i}
                    className="px-1.5 py-0.5 bg-red-50 text-destructive text-xs rounded flex items-center gap-1"
                  >
                    <XCircle className="w-3 h-3" />
                    {job}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={run.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              title="Open in GitHub"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={() => onInvestigate(run)}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
            >
              <Search className="w-3.5 h-3.5" />
              Investigate
            </button>
            {run.jobs && run.jobs.length > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded jobs and connections view */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gray-50 space-y-4">
          {/* Jobs section */}
          {run.jobs && run.jobs.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-2">Jobs</h5>
              <div className="space-y-2">
                {run.jobs.map((job, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {job.conclusion === 'success' ? (
                      <CheckCircle2 className="w-4 h-4 text-success" />
                    ) : job.conclusion === 'failure' ? (
                      <XCircle className="w-4 h-4 text-destructive" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-gray-700">{job.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connected PRs section */}
          {connections?.nodes?.filter(n => n.node_type === 'GitHubPR').length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <GitPullRequest className="w-3.5 h-3.5" />
                Linked Pull Requests
              </h5>
              <div className="space-y-1">
                {connections.nodes.filter(n => n.node_type === 'GitHubPR').map((pr, i) => (
                  <a
                    key={i}
                    href={pr.properties?.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-info hover:text-info"
                  >
                    <GitPullRequest className="w-3.5 h-3.5" />
                    <span>#{pr.properties?.number}: {pr.properties?.title?.substring(0, 50)}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Connected Issues section */}
          {connections?.nodes?.filter(n => n.node_type === 'GitHubIssue').length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <CircleDot className="w-3.5 h-3.5" />
                Linked Issues
              </h5>
              <div className="space-y-1">
                {connections.nodes.filter(n => n.node_type === 'GitHubIssue').map((issue, i) => (
                  <a
                    key={i}
                    href={issue.properties?.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-info hover:text-info"
                  >
                    <CircleDot className="w-3.5 h-3.5" />
                    <span>#{issue.properties?.number}: {issue.properties?.title?.substring(0, 50)}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sync source card
function SyncSourceCard({ name, icon: Icon, iconComponent: IconComponent, connected, lastSync, itemCount, onSync, syncing, color = 'purple', onClick, needsConfig }) {
  const colorClasses = {
    purple: 'bg-primary/10 text-primary',
    blue: 'bg-info/10 text-info',
    green: 'bg-success/10 text-success',
    gray: 'bg-gray-900 text-white',
  };

  // Show "needs config" when connected but hasn't selected repos/channels
  const showNeedsConfig = connected && needsConfig;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {IconComponent ? <IconComponent className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{name}</h4>
            <p className="text-xs text-gray-500">
              {connected ? `Last sync: ${formatTimeAgo(lastSync)}` : 'Not connected'}
            </p>
          </div>
        </div>
        {connected && itemCount !== undefined && (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${itemCount > 0 ? 'bg-success/10 text-success' : 'bg-amber-100 text-amber-700'}`}>
            {itemCount} items
          </span>
        )}
      </div>

      {connected ? (
        showNeedsConfig ? (
          // Connected but needs to select repos/channels
          <button
            onClick={onClick}
            className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <Settings className="w-4 h-4" />
            Select {name === 'GitHub' ? 'Repositories' : name === 'Slack' ? 'Channels' : 'Items'}
          </button>
        ) : (
          // Connected and has items - show sync button
          <div className="flex gap-2">
            <button
              onClick={onSync}
              disabled={syncing}
              className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
            {onClick && (
              <button
                onClick={onClick}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>
        )
      ) : (
        <button
          onClick={onClick}
          className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-sm"
        >
          Connect in Integrations
        </button>
      )}
    </div>
  );
}

// Item detail modal
function ItemDetailModal({ item, onClose }) {
  if (!item) return null;
  const isPR = item.type === 'pull_request';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isPR ? 'bg-success/10' : 'bg-info/10'}`}>
              {isPR ? (
                <GitPullRequest className="w-5 h-5 text-success" />
              ) : (
                <CircleDot className="w-5 h-5 text-info" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{item.repo}</span>
                <span className="text-sm text-gray-400">#{item.number}</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{item.title}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex items-center gap-4">
            <StateBadge state={item.state} merged={item.merged} />
            {item.labels?.map((label, i) => (
              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                {label}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Author:</span>
              <span className="ml-2 font-medium">{item.author}</span>
            </div>
            <div>
              <span className="text-gray-500">Updated:</span>
              <span className="ml-2 font-medium">{formatTimeAgo(item.updated_at)}</span>
            </div>
            {isPR && (
              <>
                <div>
                  <span className="text-gray-500">Base:</span>
                  <span className="ml-2 font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">{item.base_ref}</span>
                </div>
                <div>
                  <span className="text-gray-500">Head:</span>
                  <span className="ml-2 font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">{item.head_ref}</span>
                </div>
              </>
            )}
          </div>

          {item.body && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.body}</p>
            </div>
          )}

          {isPR && (
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-success">+{item.additions || 0}</p>
                <p className="text-xs text-gray-500">Additions</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-destructive">-{item.deletions || 0}</p>
                <p className="text-xs text-gray-500">Deletions</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-700">{item.changed_files || 0}</p>
                <p className="text-xs text-gray-500">Files</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 font-medium rounded-lg"
          >
            Close
          </button>
          <a
            href={item.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Open in GitHub
          </a>
        </div>
      </div>
    </div>
  );
}

// GitLab Logo SVG
function GitLabLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z"/>
    </svg>
  );
}

export default function DataSyncPanel({ onInvestigate, onNavigateToIntegrations }) {
  const [slackStatus, setSlackStatus] = useState(null);
  const [githubStatus, setGithubStatus] = useState(null);
  const [gitlabStatus, setGitlabStatus] = useState(null);
  const [jiraStatus, setJiraStatus] = useState(null);
  const [linearStatus, setLinearStatus] = useState(null);
  const [buildkiteStatus, setBuildkiteStatus] = useState(null);
  const [criblStatus, setCriblStatus] = useState(null);
  const [pagerdutyStatus, setPagerdutyStatus] = useState(null);
  const [threads, setThreads] = useState([]);
  const [githubItems, setGithubItems] = useState([]);
  const [gitlabItems, setGitlabItems] = useState([]);
  const [jiraItems, setJiraItems] = useState([]);
  const [linearItems, setLinearItems] = useState([]);
  const [buildkiteItems, setBuildkiteItems] = useState([]);
  const [criblItems, setCriblItems] = useState([]);
  const [pagerdutyItems, setPagerdutyItems] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [dependencyGraph, setDependencyGraph] = useState({ nodes: [], edges: [] });
  const [channels, setChannels] = useState([]);
  // Total counts from API (not limited by fetch limit)
  const [githubTotal, setGithubTotal] = useState(0);
  const [gitlabTotal, setGitlabTotal] = useState(0);
  const [jiraTotal, setJiraTotal] = useState(0);
  const [linearTotal, setLinearTotal] = useState(0);
  const [buildkiteTotal, setBuildkiteTotal] = useState(0);
  const [criblTotal, setCriblTotal] = useState(0);
  const [pagerdutyTotal, setPagerdutyTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncingSlack, setSyncingSlack] = useState(false);
  const [syncingGithub, setSyncingGithub] = useState(false);
  const [syncingGitlab, setSyncingGitlab] = useState(false);
  const [syncingJira, setSyncingJira] = useState(false);
  const [syncingLinear, setSyncingLinear] = useState(false);
  const [syncingBuildkite, setSyncingBuildkite] = useState(false);
  const [syncingCribl, setSyncingCribl] = useState(false);
  const [syncingPagerduty, setSyncingPagerduty] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [activeTab, setActiveTab] = useState('pulls');
  const [activeSource, setActiveSource] = useState('github');
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Investigation modal state
  const [investigationItem, setInvestigationItem] = useState(null);
  const [investigationItemType, setInvestigationItemType] = useState('pr');
  const [showInvestigationModal, setShowInvestigationModal] = useState(false);

  // Causal Chain Analysis state
  const [showCausalChainAnalysis, setShowCausalChainAnalysis] = useState(false);
  const [causalChainData, setCausalChainData] = useState(null);
  const [causalChainLoading, setCausalChainLoading] = useState(false);

  // GitHub filters
  const [githubSearch, setGithubSearch] = useState('');
  const [githubState, setGithubState] = useState('all');
  const [githubRepo, setGithubRepo] = useState('all');

  // Helper to get connections for an entity from the dependency graph
  const getEntityConnections = useCallback((entityId) => {
    if (!dependencyGraph || !dependencyGraph.edges) return { edges: [], nodes: [] };

    const relatedEdges = dependencyGraph.edges.filter(
      e => e.source === entityId || e.target === entityId
    );

    const connectedNodeIds = new Set();
    relatedEdges.forEach(e => {
      if (e.source !== entityId) connectedNodeIds.add(e.source);
      if (e.target !== entityId) connectedNodeIds.add(e.target);
    });

    const relatedNodes = (dependencyGraph.nodes || []).filter(
      n => connectedNodeIds.has(n.id)
    );

    return { edges: relatedEdges, nodes: relatedNodes };
  }, [dependencyGraph]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [slackRes, githubRes, gitlabRes, jiraRes, linearRes, buildkiteRes, criblRes, pagerdutyRes] = await Promise.all([
        api.get('/slack/status').catch(() => ({ data: { status: 'not_configured' } })),
        api.get('/github/status').catch(() => ({ data: { connected: false } })),
        api.get('/gitlab/status').catch(() => ({ data: { connected: false } })),
        api.get('/jira/status').catch(() => ({ data: { connected: false } })),
        api.get('/linear/status').catch(() => ({ data: { connected: false } })),
        api.get('/buildkite/status').catch(() => ({ data: { connected: false } })),
        api.get('/cribl/status').catch(() => ({ data: { connected: false } })),
        api.get('/pagerduty/status').catch(() => ({ data: { connected: false } }))
      ]);

      setSlackStatus(slackRes.data);
      setGithubStatus(githubRes.data);
      setGitlabStatus(gitlabRes.data);
      setJiraStatus(jiraRes.data);
      setLinearStatus(linearRes.data);
      setBuildkiteStatus(buildkiteRes.data);
      setCriblStatus(criblRes.data);
      setPagerdutyStatus(pagerdutyRes.data);

      // Fetch Slack threads if connected
      if (slackRes.data.status === 'connected') {
        try {
          const threadsRes = await api.get('/slack/threads', { params: { limit: 50 } });
          setThreads(threadsRes.data.threads || []);
        } catch (err) {
          console.error('Failed to fetch threads:', err);
        }

        if (slackRes.data.monitored_channels) {
          setSelectedChannels(slackRes.data.monitored_channels);
        }

        try {
          const channelsRes = await api.get('/slack/channels');
          setChannels(channelsRes.data.channels || []);
        } catch (err) {
          console.error('Failed to fetch channels:', err);
        }
      }

      // Fetch GitHub items if connected
      if (githubRes.data.connected) {
        try {
          const [itemsRes, actionsRes, graphRes] = await Promise.all([
            api.get('/github/items', { params: { limit: 100 } }),
            api.get('/github/workflow-runs', { params: { limit: 50 } }).catch(() => ({ data: { items: [], total: 0 } })),
            api.get('/github/dependency-graph').catch(() => ({ data: { nodes: [], edges: [] } }))
          ]);
          setGithubItems(itemsRes.data.items || []);
          setGithubTotal(itemsRes.data.total || itemsRes.data.items?.length || 0);
          setWorkflowRuns(actionsRes.data.items || []);
          setDependencyGraph(graphRes.data || { nodes: [], edges: [] });
          setActiveSource('github');
        } catch (err) {
          console.error('Failed to fetch GitHub items:', err);
        }
      }

      // Fetch GitLab items if connected
      if (gitlabRes.data.connected) {
        try {
          const gitlabItemsRes = await api.get('/gitlab/items', { params: { limit: 100 } });
          setGitlabItems(gitlabItemsRes.data.items || []);
          setGitlabTotal(gitlabItemsRes.data.total || gitlabItemsRes.data.items?.length || 0);
          // If GitHub not connected, default to GitLab
          if (!githubRes.data.connected) {
            setActiveSource('gitlab');
          }
        } catch (err) {
          console.error('Failed to fetch GitLab items:', err);
        }
      }

      // Fetch Jira items if connected
      if (jiraRes.data.connected) {
        try {
          const jiraItemsRes = await api.get('/jira/items', { params: { limit: 100 } });
          setJiraItems(jiraItemsRes.data.items || []);
          setJiraTotal(jiraItemsRes.data.total || jiraItemsRes.data.items?.length || 0);
        } catch (err) {
          console.error('Failed to fetch Jira items:', err);
        }
      }

      // Fetch Linear items if connected
      if (linearRes.data.connected) {
        try {
          const linearItemsRes = await api.get('/linear/items', { params: { limit: 100 } });
          setLinearItems(linearItemsRes.data.items || []);
          setLinearTotal(linearItemsRes.data.total || linearItemsRes.data.items?.length || 0);
        } catch (err) {
          console.error('Failed to fetch Linear items:', err);
        }
      }

      // Fetch Buildkite items if connected
      if (buildkiteRes.data.connected) {
        try {
          const buildkiteItemsRes = await api.get('/buildkite/items', { params: { limit: 100 } });
          setBuildkiteItems(buildkiteItemsRes.data.items || []);
          setBuildkiteTotal(buildkiteItemsRes.data.total || buildkiteItemsRes.data.items?.length || 0);
        } catch (err) {
          console.error('Failed to fetch Buildkite items:', err);
        }
      }

      // Fetch Cribl items if connected
      if (criblRes.data.connected) {
        try {
          const criblItemsRes = await api.get('/cribl/items', { params: { limit: 100 } });
          setCriblItems(criblItemsRes.data.items || []);
          setCriblTotal(criblItemsRes.data.total || criblItemsRes.data.items?.length || 0);
        } catch (err) {
          console.error('Failed to fetch Cribl items:', err);
        }
      }

      // Fetch PagerDuty incidents if connected
      if (pagerdutyRes.data.connected) {
        try {
          const pagerdutyItemsRes = await api.get('/pagerduty/incidents', { params: { limit: 100 } });
          setPagerdutyItems(pagerdutyItemsRes.data.incidents || []);
          setPagerdutyTotal(pagerdutyItemsRes.data.total || pagerdutyItemsRes.data.incidents?.length || 0);
        } catch (err) {
          console.error('Failed to fetch PagerDuty incidents:', err);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);

    // Listen for OAuth popup completion
    const handleOAuthMessage = async (event) => {
      if (event.data?.type === 'oauth_complete') {
        console.log('OAuth complete in DataSync:', event.data);
        await fetchData();
      }
    };
    window.addEventListener('message', handleOAuthMessage);

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, [fetchData]);

  // Sync Slack
  const handleSlackSync = async () => {
    setSyncingSlack(true);
    setError(null);
    try {
      await api.post('/slack/sync', { since_hours: 24 });
      setTimeout(async () => {
        await fetchData();
        setSyncingSlack(false);
      }, 2000);
    } catch (err) {
      console.error('Slack sync failed:', err);
      setError('Failed to sync Slack. Please try again.');
      setSyncingSlack(false);
    }
  };

  // Sync GitHub
  const handleGithubSync = async () => {
    setSyncingGithub(true);
    setError(null);
    try {
      await api.post('/github/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingGithub(false);
      }, 2000);
    } catch (err) {
      console.error('GitHub sync failed:', err);
      setError('Failed to sync GitHub. Please try again.');
      setSyncingGithub(false);
    }
  };

  // Sync GitLab
  const handleGitlabSync = async () => {
    setSyncingGitlab(true);
    setError(null);
    try {
      await api.post('/gitlab/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingGitlab(false);
      }, 2000);
    } catch (err) {
      console.error('GitLab sync failed:', err);
      setError('Failed to sync GitLab. Please try again.');
      setSyncingGitlab(false);
    }
  };

  // Sync Jira
  const handleJiraSync = async () => {
    setSyncingJira(true);
    setError(null);
    try {
      await api.post('/jira/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingJira(false);
      }, 2000);
    } catch (err) {
      console.error('Jira sync failed:', err);
      setError('Failed to sync Jira. Please try again.');
      setSyncingJira(false);
    }
  };

  // Sync Linear
  const handleLinearSync = async () => {
    setSyncingLinear(true);
    setError(null);
    try {
      await api.post('/linear/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingLinear(false);
      }, 2000);
    } catch (err) {
      console.error('Linear sync failed:', err);
      setError('Failed to sync Linear. Please try again.');
      setSyncingLinear(false);
    }
  };

  // Sync Buildkite
  const handleBuildkiteSync = async () => {
    setSyncingBuildkite(true);
    setError(null);
    try {
      await api.post('/buildkite/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingBuildkite(false);
      }, 2000);
    } catch (err) {
      console.error('Buildkite sync failed:', err);
      setError('Failed to sync Buildkite. Please try again.');
      setSyncingBuildkite(false);
    }
  };

  // Sync Cribl
  const handleCriblSync = async () => {
    setSyncingCribl(true);
    setError(null);
    try {
      await api.post('/cribl/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingCribl(false);
      }, 2000);
    } catch (err) {
      console.error('Cribl sync failed:', err);
      setError('Failed to sync Cribl. Please try again.');
      setSyncingCribl(false);
    }
  };

  // Sync PagerDuty
  const handlePagerDutySync = async () => {
    setSyncingPagerduty(true);
    setError(null);
    try {
      await api.post('/pagerduty/sync');
      setTimeout(async () => {
        await fetchData();
        setSyncingPagerduty(false);
      }, 2000);
    } catch (err) {
      console.error('PagerDuty sync failed:', err);
      setError('Failed to sync PagerDuty. Please try again.');
      setSyncingPagerduty(false);
    }
  };

  // Save channel configuration
  const handleSaveChannels = async () => {
    try {
      await api.post('/slack/channels/configure', {
        channel_ids: selectedChannels
      });
      await fetchData();
    } catch (err) {
      console.error('Failed to save channels:', err);
      setError('Failed to save channel configuration');
    }
  };

  // Handle investigate
  const handleInvestigateThread = (thread) => {
    const query = thread.title || thread.key_findings || `Investigate incident in ${thread.channel}`;
    if (onInvestigate) {
      onInvestigate(query);
    }
  };

  const handleInvestigateGithubItem = (item) => {
    // Open the investigation modal with detailed analysis
    setInvestigationItem(item);

    // Determine item type based on item properties
    let itemType = 'pr';
    if (item.type === 'pull_request') {
      itemType = 'pr';
    } else if (item.type === 'issue') {
      itemType = 'issue';
    } else if (item.workflow_name || item.run_number || item.conclusion !== undefined) {
      // Workflow runs have workflow_name, run_number, or conclusion
      itemType = 'workflow_run';
    }

    setInvestigationItemType(itemType);
    setShowInvestigationModal(true);
  };

  // Handle LLM-powered causal chain investigation
  const handleCausalChainInvestigation = async (query) => {
    setCausalChainLoading(true);
    setShowCausalChainAnalysis(true);

    try {
      const response = await api.post('/investigate/llm', { query });
      setCausalChainData(response.data);
    } catch (err) {
      console.error('Causal chain investigation failed:', err);
      // Still show the modal with error state
      setCausalChainData({
        query,
        error: err.response?.data?.detail || 'Investigation failed',
        llm_enabled: false
      });
    } finally {
      setCausalChainLoading(false);
    }
  };

  // Handle follow-up questions in causal chain analysis
  const handleAskFollowUp = async (question) => {
    try {
      const response = await api.post('/investigate/ask', {
        question,
        context: causalChainData
      });
      return response.data.answer;
    } catch (err) {
      console.error('Follow-up question failed:', err);
      return `Error: ${err.response?.data?.detail || 'Failed to get answer'}`;
    }
  };

  // Filter GitHub items
  const filteredGithubItems = githubItems.filter(item => {
    const matchesType = activeTab === 'all' ||
      (activeTab === 'pulls' && item.type === 'pull_request') ||
      (activeTab === 'issues' && item.type === 'issue');
    const matchesState = githubState === 'all' || item.state === githubState;
    const matchesRepo = githubRepo === 'all' || item.repo === githubRepo;
    const matchesSearch = !githubSearch ||
      item.title.toLowerCase().includes(githubSearch.toLowerCase()) ||
      (item.body && item.body.toLowerCase().includes(githubSearch.toLowerCase()));
    return matchesType && matchesState && matchesRepo && matchesSearch;
  });

  // Get unique repos for filter
  const uniqueRepos = [...new Set(githubItems.map(i => i.repo))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-info animate-spin" />
      </div>
    );
  }

  const slackConnected = slackStatus?.status === 'connected' || slackStatus?.workspace?.team_id;
  const githubConnected = githubStatus?.connected;
  const gitlabConnected = gitlabStatus?.connected;
  const jiraConnected = jiraStatus?.connected;
  const linearConnected = linearStatus?.connected;
  const buildkiteConnected = buildkiteStatus?.connected;
  const criblConnected = criblStatus?.connected;
  const pagerdutyConnected = pagerdutyStatus?.connected;
  const hasAnyConnection = slackConnected || githubConnected || gitlabConnected || jiraConnected || linearConnected || buildkiteConnected || criblConnected || pagerdutyConnected;

  // Not connected state
  if (!hasAnyConnection) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Data Sync</h2>
            <p className="text-gray-500 mt-1">Sync incident data from connected integrations</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
            <Database className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data Sources Connected</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Connect an integration like GitHub or Slack to start syncing data for investigation.
          </p>
          {onNavigateToIntegrations && (
            <button
              onClick={onNavigateToIntegrations}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Set Up Integrations
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Item detail modal */}
      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Sync</h2>
          <p className="text-gray-500 mt-1">Manage synced data from your integrations</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (githubConnected) handleGithubSync();
              if (gitlabConnected) handleGitlabSync();
              if (slackConnected) handleSlackSync();
              if (jiraConnected) handleJiraSync();
              if (linearConnected) handleLinearSync();
              if (buildkiteConnected) handleBuildkiteSync();
              if (criblConnected) handleCriblSync();
              if (pagerdutyConnected) handlePagerDutySync();
            }}
            disabled={syncingSlack || syncingGithub || syncingGitlab || syncingJira || syncingLinear || syncingBuildkite || syncingCribl || syncingPagerduty}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncingSlack || syncingGithub || syncingGitlab || syncingJira || syncingLinear || syncingBuildkite || syncingCribl || syncingPagerduty ? 'animate-spin' : ''}`} />
            Sync All
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-destructive" />
          <span className="text-destructive">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-destructive hover:text-destructive">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <GitPullRequest className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {githubStatus?.metrics?.prs_ingested || githubItems.filter(i => i.type === 'pull_request').length}
              </p>
              <p className="text-sm text-gray-500">Pull Requests</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-info/10 rounded-lg">
              <CircleDot className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {githubStatus?.metrics?.issues_ingested || githubItems.filter(i => i.type === 'issue').length}
              </p>
              <p className="text-sm text-gray-500">Issues</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Play className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {githubStatus?.metrics?.workflow_runs_ingested || workflowRuns.length}
              </p>
              <p className="text-sm text-gray-500">Workflow Runs</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {slackStatus?.metrics?.messages_ingested || threads.length}
              </p>
              <p className="text-sm text-gray-500">Slack Threads</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Activity className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{dependencyGraph.edges?.length || 0}</p>
              <p className="text-sm text-gray-500">Connections</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Sources */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SyncSourceCard
          name="GitHub"
          iconComponent={GitHubLogo}
          color="gray"
          connected={githubConnected}
          lastSync={githubStatus?.metrics?.last_sync_at}
          itemCount={githubTotal || githubItems.length}
          onSync={handleGithubSync}
          syncing={syncingGithub}
          onClick={() => onNavigateToIntegrations?.('github')}
          needsConfig={!githubStatus?.repos_selected_count}
        />
        <SyncSourceCard
          name="GitLab"
          iconComponent={GitLabLogo}
          color="blue"
          connected={gitlabConnected}
          lastSync={gitlabStatus?.metrics?.last_sync_at}
          itemCount={gitlabTotal || gitlabItems.length}
          onSync={handleGitlabSync}
          syncing={syncingGitlab}
          onClick={() => onNavigateToIntegrations?.('gitlab')}
          needsConfig={gitlabConnected && (!gitlabStatus?.projects_selected_count)}
        />
        <SyncSourceCard
          name="Jira"
          iconComponent={JiraLogo}
          color="blue"
          connected={jiraConnected}
          lastSync={jiraStatus?.metrics?.last_sync_at}
          itemCount={jiraTotal || jiraItems.length}
          onSync={handleJiraSync}
          syncing={syncingJira}
          onClick={() => onNavigateToIntegrations?.('jira')}
          needsConfig={jiraConnected && (!jiraStatus?.projects_selected_count)}
        />
        <SyncSourceCard
          name="Linear"
          iconComponent={LinearLogo}
          color="purple"
          connected={linearConnected}
          lastSync={linearStatus?.metrics?.last_sync_at}
          itemCount={linearTotal || linearItems.length}
          onSync={handleLinearSync}
          syncing={syncingLinear}
          onClick={() => onNavigateToIntegrations?.('linear')}
          needsConfig={linearConnected && (!linearStatus?.teams_selected_count)}
        />
        <SyncSourceCard
          name="Buildkite"
          iconComponent={BuildkiteLogo}
          color="green"
          connected={buildkiteConnected}
          lastSync={buildkiteStatus?.metrics?.last_sync_at}
          itemCount={buildkiteTotal || buildkiteItems.length}
          onSync={handleBuildkiteSync}
          syncing={syncingBuildkite}
          onClick={() => onNavigateToIntegrations?.('buildkite')}
          needsConfig={buildkiteConnected && (!buildkiteStatus?.pipelines_selected_count)}
        />
        <SyncSourceCard
          name="Cribl"
          iconComponent={CriblLogo}
          color="blue"
          connected={criblConnected}
          lastSync={criblStatus?.metrics?.last_sync_at}
          itemCount={criblTotal || criblItems.length}
          onSync={handleCriblSync}
          syncing={syncingCribl}
          onClick={() => onNavigateToIntegrations?.('cribl')}
          needsConfig={criblConnected && (!criblStatus?.worker_groups_selected_count)}
        />
        <SyncSourceCard
          name="Slack"
          iconComponent={SlackLogo}
          color="purple"
          connected={slackConnected}
          lastSync={slackStatus?.metrics?.last_sync_at}
          itemCount={threads.length}
          onSync={handleSlackSync}
          syncing={syncingSlack}
          onClick={() => onNavigateToIntegrations?.('slack')}
          needsConfig={slackConnected && (!slackStatus?.monitored_channels || slackStatus?.monitored_channels?.length === 0)}
        />
        <SyncSourceCard
          name="PagerDuty"
          iconComponent={PagerDutyLogo}
          color="green"
          connected={pagerdutyConnected}
          lastSync={pagerdutyStatus?.metrics?.last_sync_at}
          itemCount={pagerdutyTotal || pagerdutyItems.length}
          onSync={handlePagerDutySync}
          syncing={syncingPagerduty}
          onClick={() => onNavigateToIntegrations?.('pagerduty')}
          needsConfig={pagerdutyConnected && (!pagerdutyStatus?.services_selected_count)}
        />
      </div>

      {/* Source tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6 flex-wrap">
          {githubConnected && (
            <button
              onClick={() => setActiveSource('github')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'github'
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <GitHubLogo className="w-4 h-4" />
              GitHub ({githubItems.length})
            </button>
          )}
          {gitlabConnected && (
            <button
              onClick={() => setActiveSource('gitlab')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'gitlab'
                  ? 'border-orange-600 text-warning'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <GitLabLogo className="w-4 h-4" />
              GitLab ({gitlabItems.length})
            </button>
          )}
          {jiraConnected && (
            <button
              onClick={() => setActiveSource('jira')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'jira'
                  ? 'border-blue-600 text-info'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <JiraLogo className="w-4 h-4" />
              Jira ({jiraItems.length})
            </button>
          )}
          {linearConnected && (
            <button
              onClick={() => setActiveSource('linear')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'linear'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <LinearLogo className="w-4 h-4" />
              Linear ({linearItems.length})
            </button>
          )}
          {buildkiteConnected && (
            <button
              onClick={() => setActiveSource('buildkite')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'buildkite'
                  ? 'border-green-600 text-success'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <BuildkiteLogo className="w-4 h-4" />
              Buildkite ({buildkiteItems.length})
            </button>
          )}
          {criblConnected && (
            <button
              onClick={() => setActiveSource('cribl')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'cribl'
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CriblLogo className="w-4 h-4" />
              Cribl ({criblItems.length})
            </button>
          )}
          {slackConnected && (
            <button
              onClick={() => setActiveSource('slack')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'slack'
                  ? 'border-purple-600 text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              Slack ({threads.length})
            </button>
          )}
          {pagerdutyConnected && (
            <button
              onClick={() => setActiveSource('pagerduty')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSource === 'pagerduty'
                  ? 'border-green-600 text-success'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <PagerDutyLogo className="w-4 h-4" />
              PagerDuty ({pagerdutyItems.length})
            </button>
          )}
        </div>
      </div>

      {/* GitHub content */}
      {activeSource === 'github' && githubConnected && (
        <div className="space-y-4">
          {/* Type tabs and filters */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('pulls')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'pulls'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <GitPullRequest className="w-4 h-4" />
                Pull Requests
              </button>
              <button
                onClick={() => setActiveTab('issues')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'issues'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <CircleDot className="w-4 h-4" />
                Issues
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                  activeTab === 'actions'
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Play className="w-4 h-4" />
                Actions
              </button>
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={githubSearch}
                  onChange={(e) => setGithubSearch(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
                />
              </div>
              <select
                value={githubState}
                onChange={(e) => setGithubState(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All states</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
              {uniqueRepos.length > 1 && (
                <select
                  value={githubRepo}
                  onChange={(e) => setGithubRepo(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All repos</option>
                  {uniqueRepos.map(repo => (
                    <option key={repo} value={repo}>{repo}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Items list */}
          <div className="space-y-3">
            {activeTab === 'actions' ? (
              // Workflow runs list
              workflowRuns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Play className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No workflow runs found.</p>
                  <button
                    onClick={handleGithubSync}
                    className="mt-3 text-info hover:text-info font-medium"
                  >
                    Sync now to import workflow runs
                  </button>
                </div>
              ) : (
                workflowRuns.map((run) => (
                  <WorkflowRunCard
                    key={run.id}
                    run={run}
                    onInvestigate={handleInvestigateGithubItem}
                    connections={getEntityConnections(run.id)}
                  />
                ))
              )
            ) : (
              // PRs and Issues list
              filteredGithubItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {activeTab === 'issues' ? (
                    <CircleDot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  ) : (
                    <GitPullRequest className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  )}
                  <p>
                    {activeTab === 'issues'
                      ? 'No issues found.'
                      : activeTab === 'pulls'
                        ? 'No pull requests found.'
                        : 'No items found.'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {activeTab === 'issues'
                      ? 'Issues will appear here after syncing repositories with issues.'
                      : activeTab === 'pulls'
                        ? 'Pull requests will appear here after syncing.'
                        : 'Items will appear here after syncing.'}
                  </p>
                  <button
                    onClick={handleGithubSync}
                    className="mt-3 text-info hover:text-info font-medium"
                  >
                    Sync now to import data
                  </button>
                </div>
              ) : (
                filteredGithubItems.map((item) => (
                  <GitHubItemCard
                    key={item.id}
                    item={item}
                    onInvestigate={handleInvestigateGithubItem}
                  />
                ))
              )
            )}
          </div>
        </div>
      )}

      {/* GitLab content */}
      {activeSource === 'gitlab' && gitlabConnected && (
        <div className="space-y-3">
          {gitlabItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <GitLabLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No GitLab items synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Merge requests, issues, and pipelines will appear here after syncing.
              </p>
              <button
                onClick={handleGitlabSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import data
              </button>
            </div>
          ) : (
            gitlabItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.type === 'merge_request' ? 'bg-success/10' :
                    item.type === 'issue' ? 'bg-info/10' :
                    item.type === 'pipeline' ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    {item.type === 'merge_request' ? (
                      <GitMerge className={`w-4 h-4 ${item.state === 'merged' ? 'text-primary' : item.state === 'opened' ? 'text-success' : 'text-destructive'}`} />
                    ) : item.type === 'issue' ? (
                      <CircleDot className={`w-4 h-4 ${item.state === 'opened' ? 'text-success' : 'text-primary'}`} />
                    ) : (
                      <Play className={`w-4 h-4 ${item.status === 'success' ? 'text-success' : item.status === 'failed' ? 'text-destructive' : 'text-amber-600'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{item.project_id}</span>
                      {item.iid && <span className="text-xs text-gray-400">!{item.iid}</span>}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.state === 'merged' || item.status === 'success' ? 'bg-success/10 text-success' :
                        item.state === 'opened' ? 'bg-info/10 text-info' :
                        item.status === 'failed' ? 'bg-destructive/10 text-destructive' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.state || item.status}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.title || item.ref || `Pipeline #${item.id}`}
                    </h4>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {item.author && (
                        <span className="flex items-center gap-1">
                          {item.author}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.updated_at || item.created_at)}
                      </span>
                      {item.source_branch && (
                        <span className="flex items-center gap-1">
                          <GitBranch className="w-3.5 h-3.5" />
                          {item.source_branch}
                        </span>
                      )}
                    </div>
                    {item.labels && item.labels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.labels.slice(0, 4).map((label, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.web_url && (
                      <a
                        href={item.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Open in GitLab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Jira content */}
      {activeSource === 'jira' && jiraConnected && (
        <div className="space-y-3">
          {jiraItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <JiraLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Jira issues synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Issues and epics will appear here after syncing.
              </p>
              <button
                onClick={handleJiraSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import issues
              </button>
            </div>
          ) : (
            jiraItems.map((item) => (
              <div key={item.id || item.key} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.type === 'Epic' ? 'bg-primary/10' :
                    item.type === 'Bug' ? 'bg-destructive/10' :
                    item.type === 'Story' ? 'bg-success/10' :
                    'bg-info/10'
                  }`}>
                    {item.type === 'Bug' ? (
                      <Bug className={`w-4 h-4 text-destructive`} />
                    ) : (
                      <Target className={`w-4 h-4 ${
                        item.type === 'Epic' ? 'text-primary' :
                        item.type === 'Story' ? 'text-success' :
                        'text-info'
                      }`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{item.key}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.status === 'Done' ? 'bg-success/10 text-success' :
                        item.status === 'In Progress' ? 'bg-info/10 text-info' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.status}
                      </span>
                      {item.priority && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.priority === 'Highest' || item.priority === 'High' ? 'bg-destructive/10 text-destructive' :
                          item.priority === 'Medium' ? 'bg-warning/10 text-warning' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.priority}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.summary || item.title}
                    </h4>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {item.assignee && <span>{item.assignee}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.updated)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Open in Jira"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Linear content */}
      {activeSource === 'linear' && linearConnected && (
        <div className="space-y-3">
          {linearItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <LinearLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Linear issues synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Issues and projects will appear here after syncing.
              </p>
              <button
                onClick={handleLinearSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import issues
              </button>
            </div>
          ) : (
            linearItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.priority === 1 ? 'bg-destructive/10' :
                    item.priority === 2 ? 'bg-warning/10' :
                    'bg-indigo-100'
                  }`}>
                    <Target className={`w-4 h-4 ${
                      item.priority === 1 ? 'text-destructive' :
                      item.priority === 2 ? 'text-warning' :
                      'text-indigo-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{item.identifier}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.state?.type === 'completed' ? 'bg-success/10 text-success' :
                        item.state?.type === 'started' ? 'bg-info/10 text-info' :
                        item.state?.type === 'canceled' ? 'bg-destructive/10 text-destructive' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.state?.name || item.status}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.title}
                    </h4>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {item.team && <span>{item.team.name}</span>}
                      {item.assignee && <span>{item.assignee.name}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.updatedAt)}
                      </span>
                    </div>
                    {item.labels && item.labels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.labels.slice(0, 4).map((label, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {label.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Open in Linear"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Buildkite content */}
      {activeSource === 'buildkite' && buildkiteConnected && (
        <div className="space-y-3">
          {buildkiteItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BuildkiteLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Buildkite builds synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Pipeline builds and jobs will appear here after syncing.
              </p>
              <button
                onClick={handleBuildkiteSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import builds
              </button>
            </div>
          ) : (
            buildkiteItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.state === 'passed' ? 'bg-success/10' :
                    item.state === 'failed' ? 'bg-destructive/10' :
                    item.state === 'running' ? 'bg-warning/10' :
                    'bg-gray-100'
                  }`}>
                    <Play className={`w-4 h-4 ${
                      item.state === 'passed' ? 'text-success' :
                      item.state === 'failed' ? 'text-destructive' :
                      item.state === 'running' ? 'text-warning' :
                      'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{item.pipeline?.name || item.pipeline_slug}</span>
                      <span className="text-xs text-gray-400">#{item.number}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.state === 'passed' ? 'bg-success/10 text-success' :
                        item.state === 'failed' ? 'bg-destructive/10 text-destructive' :
                        item.state === 'running' ? 'bg-warning/10 text-warning' :
                        item.state === 'canceled' ? 'bg-gray-100 text-gray-700' :
                        'bg-info/10 text-info'
                      }`}>
                        {item.state}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.message || item.commit?.message || `Build #${item.number}`}
                    </h4>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <GitBranch className="w-3.5 h-3.5" />
                        {item.branch}
                      </span>
                      {item.creator && <span>{item.creator.name}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.finished_at || item.started_at || item.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.web_url && (
                      <a
                        href={item.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Open in Buildkite"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Cribl content */}
      {activeSource === 'cribl' && criblConnected && (
        <div className="space-y-3">
          {criblItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CriblLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Cribl data synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Pipelines, routes, and events will appear here after syncing.
              </p>
              <button
                onClick={handleCriblSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import data
              </button>
            </div>
          ) : (
            criblItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.type === 'pipeline' ? 'bg-info/10' :
                    item.type === 'route' ? 'bg-primary/10' :
                    'bg-cyan-100'
                  }`}>
                    <Layers className={`w-4 h-4 ${
                      item.type === 'pipeline' ? 'text-info' :
                      item.type === 'route' ? 'text-primary' :
                      'text-cyan-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{item.type}</span>
                      {item.status && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.status === 'active' || item.status === 'running' ? 'bg-success/10 text-success' :
                          item.status === 'disabled' ? 'bg-gray-100 text-gray-700' :
                          'bg-info/10 text-info'
                        }`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.name || item.id}
                    </h4>
                    {item.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {item.worker_group && <span>Group: {item.worker_group}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.updated_at || item.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* PagerDuty content */}
      {activeSource === 'pagerduty' && pagerdutyConnected && (
        <div className="space-y-3">
          {pagerdutyItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <PagerDutyLogo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No PagerDuty incidents synced yet.</p>
              <p className="text-sm text-gray-400 mt-1">
                Incidents, services, and on-call schedules will appear here after syncing.
              </p>
              <button
                onClick={handlePagerDutySync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import incidents
              </button>
            </div>
          ) : (
            pagerdutyItems.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-lg ${
                    item.urgency === 'high' ? 'bg-destructive/10' :
                    item.status === 'resolved' ? 'bg-success/10' :
                    item.status === 'acknowledged' ? 'bg-warning/10' :
                    'bg-warning/10'
                  }`}>
                    <AlertCircle className={`w-4 h-4 ${
                      item.urgency === 'high' ? 'text-destructive' :
                      item.status === 'resolved' ? 'text-success' :
                      item.status === 'acknowledged' ? 'text-warning' :
                      'text-warning'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">#{item.incident_number || item.id?.slice(-6)}</span>
                      {item.status && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.status === 'triggered' ? 'bg-destructive/10 text-destructive' :
                          item.status === 'acknowledged' ? 'bg-warning/10 text-warning' :
                          item.status === 'resolved' ? 'bg-success/10 text-success' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.status}
                        </span>
                      )}
                      {item.urgency && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.urgency === 'high' ? 'bg-destructive/10 text-destructive' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.urgency} urgency
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.title || item.summary || 'Incident'}
                    </h4>
                    {item.service?.summary && (
                      <p className="text-xs text-gray-500 mt-1">Service: {item.service.summary}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {item.assignees && item.assignees.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {item.assignees.map(a => a.summary).join(', ')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(item.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.html_url && (
                      <a
                        href={item.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-success rounded-lg hover:bg-green-50"
                        title="Open in PagerDuty"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleInvestigateGithubItem(item)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Investigate
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Slack content */}
      {activeSource === 'slack' && slackConnected && (
        <div className="space-y-3">
          {threads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No messages synced yet.</p>
              <button
                onClick={handleSlackSync}
                className="mt-3 text-info hover:text-info font-medium"
              >
                Sync now to import messages
              </button>
            </div>
          ) : (
            threads.map((thread) => (
              <ThreadCard
                key={thread.id || thread.thread_ts}
                thread={thread}
                onInvestigate={handleInvestigateThread}
              />
            ))
          )}
        </div>
      )}

      {/* Investigation Modal */}
      <InvestigationModal
        isOpen={showInvestigationModal}
        onClose={() => {
          setShowInvestigationModal(false);
          setInvestigationItem(null);
        }}
        item={investigationItem}
        itemType={investigationItemType}
      />

      {/* Causal Chain Analysis Modal */}
      <CausalChainAnalysis
        isOpen={showCausalChainAnalysis}
        onClose={() => {
          setShowCausalChainAnalysis(false);
          setCausalChainData(null);
        }}
        investigationData={causalChainData}
        onAskFollowUp={handleAskFollowUp}
      />
    </div>
  );
}
